import {gql} from "@apollo/client";

import {Plan} from "@/lib/queries/billing.ts";
import {EnumItem} from "@/lib/queries/common.ts";
import {ContactGroupItems} from "@/lib/queries/contacts.ts";
import {AccountUser} from "@/lib/types/accounts.ts";
import {Alert, GlobalAlertState} from "@/lib/types/alerting.ts";
import {AccountBillingInfo} from "@/lib/types/billing.ts";
import {
  AccountServicesUsage,
  DataPoint,
  GroupCheckDownCondition,
  LegacyURL,
  MSPInterval,
  MonitoringServiceChoices,
  Service,
  ServiceDefinition,
  ServiceGroup,
  ServiceMaintenanceState,
  ServiceTag,
  StepDefinitions,
  ThirdPartyMonitoringType,
  ThirdPartyServiceComponent,
  UptimeGradeThreshold,
} from "@/lib/types/checks.ts";
import {GraphQLResponse, QueryError, ResponseStatus, ServerErrors, SettingsInfo} from "@/lib/types/common.ts";
import {
  DefaultLocationsNumber,
  MonitoringGroup,
  MonitoringGroupsArchMapperEntry,
  MonitoringServer,
} from "@/lib/types/monitoring.ts";
import {ServiceVariableGroupByCredential} from "@/lib/types/serviceVariables.ts";
import {Snippets} from "@/lib/types/website.ts";

export type ChecksQueryModelType =
  | "ServiceTag"
  | "ContactGroup"
  | "MonitoringGroup"
  | "MonitoringServer"
  | "GroupCheckConfig"
  | "MaintenanceSchedule"
  | "MaintenanceScheduleOccurrence";

export const GQL_CHECKS_QUERY2 = gql`
  query Services2(
    $limit: Int!
    $offset: Int!
    $sort: String!
    $search: String
    $filters: ServicesFilters
    $group_check_id: String
    $model_type: String
    $model_id: Int
    $service_ids: [Int]
  ) {
    services: services(
      limit: $limit
      offset: $offset
      sort: $sort
      search: $search
      filters: $filters
      group_check_id: $group_check_id
      model_type: $model_type
      model_id: $model_id
      service_ids: $service_ids
    ) {
      total
      stats {
        up
        down
        down_latest
        paused
        maintenance
      }
      items {
        id
        name
        msp_address
        msp_url_path
        contact_groups {
          id
          name
        }
        tags {
          id
          tag
          color_hex
        }
        uptime24h
        is_paused
        responseTimeDatapoints {
          timestamp
          value
        }
        cached_response_time
        performanceDataType
        monitoring_service_type
        cached_uptime_grade
        cached_state_is_up
        supports_downtime_alerting
        msp_response_time_sla
        is_under_maintenance
        is_under_manual_maintenance
        schedule_state
        pause_on_scheduled_maintenance
        created_at
        msp_interval
        msp_uptime_sla
        msp_port
        msp_encryption
        msp_dns_server
        msp_dns_record_type
        msp_status_code
        msp_expect_string
        msp_send_string
        msp_sensitivity
        msp_num_retries
        msp_threshold
        msp_headers
        msp_use_ip_version
        msp_username
        msp_notes
        msp_include_in_global_metrics
        cached_last_down_alert_at
        cached_last_up_alert_at
        monitoring_groups {
          id
          location
        }
        escalations {
          contact_groups {
            id
            name
          }
          wait_time
          num_repeats
        }
        group_check_services {
          id
        }
        group_check_tag_ids
      }
    }
  }
`;

export interface CheckFormResult {
  user: AccountUser;
  tags: ServiceTag[];
  snippets: Snippets;
  settingsInfo: SettingsInfo;
  contactGroups: ContactGroupItems;
  monitoringGroups: MonitoringGroup[];
  monitoringServiceTypes: ServiceGroup[];
  defaultLocationsNumber: DefaultLocationsNumber;
  serviceDefinitions: ServiceDefinition[];
  runTestMonitoringServers: MonitoringServer[];
  monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
  legacyURL: LegacyURL;
  mspInterval: MSPInterval;
  mspChoices: MonitoringServiceChoices;
  stepsDef: StepDefinitions;
  accountBillingInfo: AccountBillingInfo;
}

export const GQL_CHECK_FORM = gql`
  query CheckForm {
    user: currentUser {
      id
      has_purchase_permission
      account {
        id
        plan
        type
        limit_check_frequency
        limit_monitoring_group_level
        limit_monitoring_groups
        enable_transaction_ext_checks
      }
    }
    snippets {
      rum2_snippet
      webhook_snippet {
        url
        snippet1
        snippet2
      }
      heartbeat_snippet {
        url
        snippet1
        snippet2
      }
    }
    accountBillingInfo {
      hasSubscription
    }
    legacyURL {
      runTestURL
      pagespeedReportURL
      transactionRecorderURL
    }
    tags {
      id
      tag
      color
      color_hex
    }
    settingsInfo {
      escalation_max_repeats
    }
    contactGroups(limit: 100, offset: 0, sort: "") {
      items {
        id
        name
        email_list
        sms_list
        phonecall_list
        up_alerts_email_only
      }
    }
    monitoringGroups {
      id
      location
      code
      country
      is_private
      level
      type
    }
    runTestMonitoringServers {
      id
      group_id
      name
      address_ipv4
      address_ipv6
      hostname
      is_nagios
      is_active
      compute_scale_factor
      location
      country
      is_private
      is_test
    }
    monitoringGroupsArchMapper {
      monitoring_service_type
      version
      monitoring_group_ids
      has_nagios
      has_belgrade
    }
    defaultLocationsNumber {
      byLevel {
        level
        number
      }
      byType {
        type
        number
      }
    }
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
    mspInterval {
      min
      max
      default
    }
    serviceDefinitions {
      service_type
      verbose_name
      help_text
      versions {
        version
        name
        description
      }
      fields {
        field
        visible
        title
        optional
        default
        verbose_name
        help_text
        support_link
        advanced
        extra
        feature_flag
        mapped_fields {
          frequency
        }
      }
      support_link
      force_monitoring_group_type
      force_monitoring_group_limit
      force_interval
      performance_data_type
      performance_data_type_display
      has_feature_string_matching
      has_feature_run_test
      has_url_components
      has_transaction_script
      can_run_test_on_public
    }
    stepsDef {
      TRANSACTION
      API
      PAGESPEED
    }
    mspChoices {
      msp_sensitivity {
        value
        label
      }
      msp_num_retries {
        value
        label
      }
      msp_use_ip_version {
        value
        label
      }
      http_version {
        value
        label
      }
      http_checksum_algorithm {
        value
        label
      }
      msp_dns_record_type {
        value
        label
      }
      msp_encryption {
        value
        label
      }
      msp_expect_string_type {
        value
        label
      }
      ssl_cert_protocol {
        value
        label
      }
      ssl_cert_minimum_ssl_tls_version {
        value
        label
      }
      group_check_down_condition {
        value
        label
      }
      group_uptime_percent_calculation {
        value
        label
      }
      group_response_time_calculation_mode {
        value
        label
      }
      group_response_time_check_type {
        value
        label
      }
      connection_throttling {
        value
        label
      }
      emulated_device {
        value
        label
      }
      uptime_grade_threshold {
        value
        label
      }
      aggregation_type {
        value
        label
      }
    }
  }
`;

export const GQL_SERVICE_MAINTENANCE_STATE = gql`
  query ServiceMaintenanceStates($include: [Int!]!) {
    serviceMaintenanceStates(include: $include) {
      items {
        id
        name
        is_under_manual_maintenance
        pause_under_manual_maintenance
        maintenance_schedule_ids
      }
      total
    }
  }
`;

export interface ServiceMaintenanceStateResult {
  items: ServiceMaintenanceState[];
  total: number;
}

export interface ServiceMaintenanceStateResponse {
  serviceMaintenanceStates: ServiceMaintenanceStateResult;
}

export interface AccountUsageResponse {
  accountServicesUsage: AccountServicesUsage;
  plan: Plan;
}

export const GQL_ACCOUNT_USAGE = gql`
  query AccountServiceUsage {
    plan {
      name
      limits {
        limit_transaction_checks
        limit_api_checks
        limit_basic_checks
        limit_custom_checks
        limit_group_checks
        limit_pagespeed_checks
        limit_cloudstatus_checks
      }
    }
    accountServicesUsage {
      HTTP {
        ...ServiceUsageFields
      }
      RUM2 {
        ...ServiceUsageFields
      }
      ICMP {
        ...ServiceUsageFields
      }
      SSH {
        ...ServiceUsageFields
      }
      TCP {
        ...ServiceUsageFields
      }
      UDP {
        ...ServiceUsageFields
      }
      DNS {
        ...ServiceUsageFields
      }
      SMTP {
        ...ServiceUsageFields
      }
      POP {
        ...ServiceUsageFields
      }
      IMAP {
        ...ServiceUsageFields
      }
      NTP {
        ...ServiceUsageFields
      }
      BLACKLIST {
        ...ServiceUsageFields
      }
      WHOIS {
        ...ServiceUsageFields
      }
      RDAP {
        ...ServiceUsageFields
      }
      MALWARE {
        ...ServiceUsageFields
      }
      SSL_CERT {
        ...ServiceUsageFields
      }
      TRANSACTION {
        ...ServiceUsageFields
      }
      API {
        ...ServiceUsageFields
      }
      HEARTBEAT {
        ...ServiceUsageFields
      }
      WEBHOOK {
        ...ServiceUsageFields
      }
      GROUP {
        ...ServiceUsageFields
      }
      PAGESPEED {
        ...ServiceUsageFields
      }
      CLOUDSTATUS {
        ...ServiceUsageFields
      }
      FTP {
        ...ServiceUsageFields
      }
      SFTP {
        ...ServiceUsageFields
      }
      NETWORK {
        ...ServiceUsageFields
      }
    }
  }
`;

interface DNSLookUp {
  success: boolean;
  errors: ServerErrors;
  dnsServer: string;
  expectString: string;
}

export interface DNSLookUpResponse {
  dnsLookup: DNSLookUp;
}

export const GQL_DNS_LOOKUP = gql`
  query DNSLookup($domain: String!, $recordType: String!, $nameserver: String) {
    dnsLookup(domain: $domain, recordType: $recordType, nameserver: $nameserver) {
      success
      errors
      dnsServer
      expectString
    }
  }
`;

interface WhoIsLookUp {
  success: boolean;
  errors: ServerErrors;
  expectString: string;
}

export interface WhoIsLookUpResponse {
  whoisLookup: WhoIsLookUp;
}

export const GQL_WHOIS_LOOKUP = gql`
  query WhoIsLookup($domain: String!) {
    whoisLookup(domain: $domain) {
      success
      errors
      expectString
    }
  }
`;

export type UpdateCheckResponse = GraphQLResponse<Service>;

export const GQL_UPDATE_CHECK = gql`
  mutation UpdateCheck($input: UpdateCheckInput!) {
    response: updateCheck(input: $input) {
      ... on UpdateCheckSuccess {
        status
        data {
          ...ServiceFields
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface BulkCreateChecksItemInput {
  proposal_id?: string;
  monitoring_service_type: string;
  name: string;
  msp_address?: string;
  monitoring_groups: number[];
  msp_interval?: number;
  msp_threshold?: number;
  msp_sensitivity?: string;
  msp_port?: string;
  msp_dns_record_type?: string;
  msp_expect_string?: string;
  msp_status_code?: string;
  msp_script?: string;
  uptime_grade_threshold?: UptimeGradeThreshold;
  tags?: BulkCreateChecksTagInput[];
  contact_groups?: number[];
  group_check_proposal_ids?: string[];
  group_check_services?: number[];
  group_check_down_condition?: GroupCheckDownCondition;
  group_check_id?: number;
}

export type BulkCreateChecksTagInput = {id: number} | {tag: string; color_hex: string};

export interface BulkCreateChecksInput {
  checks: BulkCreateChecksItemInput[];
  tags: BulkCreateChecksTagInput[];
  contact_groups: number[];
  thread_id?: string;
  pre_created_tags_count?: number;
}

export interface BulkCreateChecksCreated {
  proposal_id: string | null;
  id: number;
  uuid: string;
  name: string;
}

export interface BulkCreateChecksFailed {
  proposal_id: string | null;
  name: string;
  errors: ServerErrors;
}

export interface BulkCreateChecksData {
  created: BulkCreateChecksCreated[];
  failed: BulkCreateChecksFailed[];
  tags: ServiceTag[];
}

export type BulkCreateChecksResponse = GraphQLResponse<BulkCreateChecksData>;

export const GQL_BULK_CREATE_CHECKS = gql`
  mutation BulkCreateChecks($input: BulkCreateChecksInput!) {
    response: bulkCreateChecks(input: $input) {
      ... on BulkCreateChecksSuccess {
        status
        data {
          created {
            proposal_id
            id
            uuid
            name
          }
          failed {
            proposal_id
            name
            errors
          }
          tags {
            id
            tag
            color_hex
          }
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface FindCheckResponse {
  serviceVariables: {data: ServiceVariableGroupByCredential[]};
  findCheck: Service;
}

export const GQL_EDIT_CHECK_DATA = gql`
  query EditCheckData($id: Int!) {
    findCheck(id: $id) {
      ...ServiceFields
    }
    serviceVariables: serviceVariableByCredentialId(service_id: $id) {
      data {
        credential {
          id
          account
          credential_type
          display_name
          description
          hint
          username
          version
          used_secret_properties
        }
        service_variables {
          id
          account
          service
          credential {
            id
            account
            credential_type
            display_name
            description
            hint
            username
            version
            used_secret_properties
          }
          variable_name
          property_name
        }
      }
    }
  }
`;

export const GQL_DELETE_CHECKS = gql`
  mutation deleteChecks($ids: [Int!]!) {
    response: deleteChecks(ids: $ids) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_CHECK_IS_PAUSED = gql`
  mutation updateChecksIsPaused($ids: [Int!]!, $is_paused: Boolean!, $send_resolved_notifications: Boolean) {
    response: updateChecksIsPaused(
      ids: $ids
      is_paused: $is_paused
      send_resolved_notifications: $send_resolved_notifications
    ) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_LOCATIONS_STATE = gql`
  mutation updateChecksLocations($ids: [Int!]!, $monitoring_groups: [Int!]!) {
    response: updateChecksLocations(ids: $ids, monitoring_groups: $monitoring_groups) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_MAINTENANCE_SCHEDULE_V2 = gql`
  mutation updateChecksScheduleStateV2($ids: [Int!]!, $data: BulkUpdateServiceMaintenanceSchedules!) {
    response: updateChecksScheduleStateV2(ids: $ids, data: $data) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_INTERVALS_STATE = gql`
  mutation updateChecksIntervals($ids: [Int!]!, $msp_interval: Int!) {
    response: updateChecksIntervals(ids: $ids, msp_interval: $msp_interval) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_CONTACTS_STATE = gql`
  mutation updateChecksIntervals($ids: [Int!]!, $contact_groups: [Int!]!) {
    response: updateChecksContacts(ids: $ids, contact_groups: $contact_groups) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_UPGRADE_CHECKER_VERSION_STATE = gql`
  mutation updateChecksUpgradeCheckerVersion($ids: [Int!]!, $msp_version: Int!) {
    response: updateChecksUpgradeCheckerVersion(ids: $ids, msp_version: $msp_version) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_CHECKS_SENSITIVITY_RETRIES_AND_TIMEOUT_STATE = gql`
  mutation updateChecksSRT($input: UpdateSensitivityRetriesAndTimeoutInput!) {
    response: updateSensitivityRetriesAndTimeout(input: $input) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_SLA_STATE = gql`
  mutation updateChecksSLA($input: UpdateChecksSlaInput!) {
    response: updateChecksSLA(input: $input) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_ESCALATIONS_STATE = gql`
  mutation updateChecksEscalations($ids: [Int!]!, $escalations: [EscalationInput!]!) {
    response: updateChecksEscalations(ids: $ids, escalations: $escalations) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_TAGS_STATE = gql`
  mutation updateChecksTags($ids: [Int!]!, $tags: [Int!]!) {
    response: updateChecksTags(ids: $ids, tags: $tags) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;
export const GQL_UPDATE_GROUPED_CHECK_SERVICES = gql`
  mutation updateGroupCheckServices($ids: [Int!]!, $group_check_id: Int!) {
    response: updateGroupCheckServices(ids: $ids, group_check_id: $group_check_id) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface GetServiceTypesAndTagsResult {
  monitoringServiceTypes: ServiceGroup[];
  tags: ServiceTag[];
}

export interface GetSLAReportFormRequirements extends GetServiceTypesAndTagsResult {
  slaReportGlobalLogo: {
    logo_url: string;
  };
}

export const GQL_GET_SLA_REPORT_FORM_REQUIREMENTS = gql`
  query getSlaReportFormRequirements {
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
    tags {
      id
      tag
      color
      color_hex
    }
    slaReportGlobalLogo {
      logo_url
    }
  }
`;

export interface MonitoringGroups {
  monitoringGroups: MonitoringGroup[];
}
export const GQL_GET_MONITORING_GROUPS = gql`
  query MonitoringGroups {
    monitoringGroups {
      id
      location
      code
      country
      is_private
      level
      type
    }
  }
`;

export interface MspInterval {
  mspInterval: MSPInterval;
}
export const GQL_GET_MSP_INTERVALS_GROUPS = gql`
  query MspIntervals {
    mspInterval {
      min
      max
      default
    }
  }
`;

export interface MspChoices {
  mspChoices: {
    msp_sensitivity: EnumItem[];
    msp_num_retries: EnumItem[];
  };
}
export const GQL_GET_MSP_CHOICES_GROUPS = gql`
  query contactGroups {
    mspChoices {
      msp_sensitivity {
        value
        label
      }
      msp_num_retries {
        value
        label
      }
    }
  }
`;

export const GQL_GET_SERVICE_DEFINITIONS = gql`
  query getServiceDefinitions {
    serviceDefinitions {
      service_type
      verbose_name
      help_text
      versions {
        version
        name
        description
      }
      fields {
        field
        visible
        title
        optional
        default
        verbose_name
        help_text
        support_link
        advanced
        extra
        feature_flag
        mapped_fields {
          frequency
        }
      }
      support_link
      force_monitoring_group_type
      force_monitoring_group_limit
      force_interval
      performance_data_type
      performance_data_type_display
      has_feature_string_matching
      has_feature_run_test
      has_url_components
      has_transaction_script
      can_run_test_on_public
    }
  }
`;

interface BulkImportData {
  number: number;
}

export type BulkImportResponse = GraphQLResponse<BulkImportData>;

export const GQL_BULK_IMPORT = gql`
  mutation BulkImport($file: Upload!) {
    response: bulkImport(file: $file) {
      ... on BulkImportSuccess {
        status
        data {
          number
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface ThirdPartyService {
  id: number;
  group: string;
  logo_src: string;
  components_count?: number;
  main_url?: string;
  status_page_url?: string;
  social_url?: string;
}

interface CloudStatusItems {
  total: number;
  items: ThirdPartyServiceComponent[];
}

interface CloudStatusGroups {
  total: number;
  items: ThirdPartyService[];
}

export interface CloudStatusList {
  cloudStatusMetas: CloudStatusItems;
}

export enum CloudStatusComponentSortOrder {
  LATEST = "LATEST",
  OLDEST = "OLDEST",
  A_Z = "A_Z",
  Z_A = "Z_A",
}

export interface ThirdPartySubComponent {
  id: string;
  sub_title: string;
  display_sub_title?: string;
}

export interface ThirdPartyComponent {
  id: string;
  title?: string;
  display_title?: string;
  sub_title?: string;
  display_sub_title?: string;
  sub_title_count?: number;
  match_count?: number;
  match_list?: ThirdPartySubComponent[];
  sub_component_monitoring_type?: ThirdPartyMonitoringType;
  sub_components?: ThirdPartySubComponent[];
}

interface ThirdPartyComponents {
  total: number;
  items: ThirdPartyComponent[];
}

export interface ThirdPartyComponentList {
  cloudStatusComponents: ThirdPartyComponents;
}

export interface CloudStatusGroupList {
  cloudStatusGroups: CloudStatusGroups;
}

export interface CloudStatusComponentHistoriesResponse {
  cloudStatusCheckComponentHistories: {
    total: number;
    items: ThirdPartyServiceComponent[];
  };
}

export const GQL_CLOUD_STATUS_CHECK_COMPONENT_HISTORIES = gql`
  query cloudStatusCheckComponentHistories(
    $serviceId: Int!
    $limit: Int!
    $offset: Int!
    $orderBy: CloudStatusComponentSortOrder
    $search: String
  ) {
    cloudStatusCheckComponentHistories(
      serviceId: $serviceId
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      search: $search
    ) {
      total
      items {
        name
        group
        title
        display_title
        sub_title
        display_sub_title
        is_up
        sub_status
        current_incident
        history
        feed_url
        logo_src
        main_url
        status_page_url
        social_url
      }
    }
  }
`;

export interface CloudStatusMetasCount {
  cloudStatusMetasCount: {
    count: number;
  };
}

export const GQL_THIRD_PARTY_COMPONENTS = gql`
  query cloudStatusMetas($limit: Int!, $search: String!, $group: String!, $excludeIds: [Int!]) {
    cloudStatusMetas(limit: $limit, search: $search, group: $group, excludeIds: $excludeIds) {
      total
      items {
        name
        title
        display_title
        sub_title
        id
        group
        logo_src
      }
    }
  }
`;

export const GQL_THIRD_PARTY_COMPONENTS_V2 = gql`
  query cloudStatusComponents(
    $limit: Int!
    $offset: Int
    $group: Int!
    $title: String
    $subtitle: String
    $excludeIds: [String!]
    $excludeTitles: [String!]
  ) {
    cloudStatusComponents(
      limit: $limit
      offset: $offset
      group: $group
      title: $title
      sub_title: $subtitle
      exclude_ids: $excludeIds
      exclude_titles: $excludeTitles
    ) {
      total
      items {
        id
        title
        display_title
        sub_title
        display_sub_title
        sub_title_count
        match_count
        match_list {
          id
          sub_title
          display_sub_title
        }
      }
    }
  }
`;

export const GQL_THIRD_PARTY_COMPONENTS_COUNT = gql`
  query cloudStatusMetasCount {
    cloudStatusMetasCount {
      count
    }
  }
`;

export const GQL_THIRD_PARTY_GROUPS = gql`
  query cloudStatusGroups($limit: Int!, $offset: Int, $search: String, $orderBy: String) {
    cloudStatusGroups(limit: $limit, offset: $offset, search: $search, orderBy: $orderBy) {
      total
      items {
        id
        group
        logo_src
        components_count
      }
    }
  }
`;

export interface TagsResponse {
  tags: ServiceTag[];
}

export const GQL_TAGS = gql`
  query Tags {
    tags {
      id
      tag
      color_hex
      color
      usage_count
    }
  }
`;

export interface ServiceTagsQueryResponse {
  items: ServiceTag[];
  total: number;
}

export interface ServiceTagsResponse {
  serviceTags: ServiceTagsQueryResponse;
}

export const GQL_UPDATE_TAGS = gql`
  mutation updateTags($input: [TagInput!]!) {
    response: updateTags(input: $input) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_SERVICE_TAGS = gql`
  query ServiceTags($offset: Int, $limit: Int, $sort: String, $search: String) {
    serviceTags(offset: $offset, limit: $limit, sort: $sort, search: $search) {
      items {
        id
        tag
        color_hex
        usage_count
      }
      total
    }
  }
`;

export const GQL_SAVE_TAG = gql`
  mutation saveTag($tag: TagInput!) {
    response: saveTag(tag: $tag) {
      ... on SaveTagSuccess {
        status
        tag {
          id
          tag
          color_hex
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface SaveTagResponse {
  response: {status: ResponseStatus.SUCCESS; tag: ServiceTag} | QueryError;
}

export const GQL_DELETE_TAGS = gql`
  mutation deleteTags($ids: [Int!]!) {
    response: deleteTags(ids: $ids) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface LatestDowntime extends GlobalAlertState {
  is_currently_down: boolean;
  ended_at?: string;
  // can be calculated from ended_at|now - created_at
  duration: number;
}

export interface CheckDetailsAlert {
  state_is_up: boolean;
  created_at: string;
  ended_at?: string;
  output: string;
  ignored: boolean;
  duration: number;
  id: number;
}

export interface CheckInterval {
  interval_start: string;
  interval_end: string;
  is_down_at_start: boolean;
  is_down_at_end: boolean;
  alerts: CheckDetailsAlert[];
}

export interface LocationResponseTimes {
  id: string;
  data: DataPoint[];
}

// A location's display name is its identifier for both POP and GlobalPing origins.
export interface CheckStatsLocation {
  id: string;
  location: string;
}

export enum CheckStateEventStatus {
  UP = "UP",
  DOWN = "DOWN",
  NO_DATA = "NO_DATA",
}

export interface CheckStateEventWindow {
  start: string;
  end?: string;
  status: CheckStateEventStatus;
  detail?: string;
  alert_id?: number;
}

interface CheckStats {
  totals: {
    uptime_pct?: number;
    downtime_secs: number;
    outages: number;
    response_time_max?: number;
    response_time_min?: number;
    response_time_avg?: number;
  };
  intervals: CheckInterval[];
  // flatten alerts representation into up & down state, top 5 alerts
  flatten_alerts: CheckDetailsAlert[];
  flatten_alerts_total: number;
  available_locations: CheckStatsLocation[];
  location_response_times: LocationResponseTimes[];
  check_state_events: CheckStateEventWindow[];
}

export interface CheckAlertsListResponse {
  items: GlobalAlertState[];
  total: number;
}

export interface CheckDetailsResponse {
  service: Service;
  serviceDefinitions: ServiceDefinition[];
  latestDowntime: LatestDowntime;
  stats: CheckStats;
  alerts: CheckAlertsListResponse;
}

export const GQL_CHECK_DETAILS = gql`
  query checkDetails($id: Int!, $start: DateTime!, $end: DateTime!, $location: Int) {
    service: findCheck(id: $id) {
      id
      name
      msp_address
      msp_interval
      msp_url_path
      created_at
      is_paused
      is_under_maintenance
      schedule_state
      pause_on_scheduled_maintenance
      cached_state_is_up
      cached_response_time
      msp_uptime_sla
      msp_interval
      monitoring_service_type
      public_check_details_url
      selected_third_party_service_component {
        name
        group
        title
        sub_title
        is_up
        sub_status
        current_incident
        history
        feed_url
        logo_src
        main_url
        status_page_url
        social_url
      }
      third_party_group {
        id
        group
        logo_src
        main_url
        status_page_url
        social_url
      }
      third_party_monitoring_type
      device {
        id
        name
        address
      }
      tags {
        id
        tag
        color_hex
      }
      supports_downtime_alerting
    }
    alerts(service_id: $id) {
      total
      items {
        created_at
      }
    }
    stats: getCheckStats(input: {id: $id, start: $start, end: $end, location: $location}) {
      totals {
        uptime_pct
        downtime_secs
        outages
      }
      intervals {
        interval_start
        interval_end
        is_down_at_start
        is_down_at_end
        alerts {
          id
          state_is_up
          created_at
          ended_at
          output
        }
      }
      flatten_alerts {
        state_is_up
        created_at
        output
        duration
        ignored
        id
      }
      flatten_alerts_total
      available_locations {
        id
        location
      }
      location_response_times {
        id
        data {
          timestamp
          value
        }
      }
      check_state_events {
        start
        end
        status
        detail
        alert_id
      }
    }
    serviceDefinitions {
      service_type
      force_interval
      verbose_name
      performance_data_type
    }
    latestDowntime: getLatestDowntime(checkId: $id) {
      is_currently_down
      created_at
      ended_at
      duration
    }
  }
`;

export interface LocationStatus {
  id: number;
  monitoring_group: Pick<MonitoringGroup, "id" | "location">;
  monitoring_server: Pick<MonitoringServer, "id" | "name" | "location" | "address_ipv4" | "address_ipv6">;
  is_nagios: boolean;
  latest_alert?: Pick<Alert, "id" | "state" | "created_at" | "is_up" | "output">;
}

export interface StatusPerLocationResponse {
  items: LocationStatus[];
  total: number;
}

export interface CheckRTASummaryResponse {
  service: Service;
  statusPerLocation: StatusPerLocationResponse;
  monitoringServers: MonitoringServer[];
}

export const GQL_CHECK_RTA_SUMMARY = gql`
  query checkRTASummary($id: Int!) {
    service: findCheck(id: $id) {
      id
      name
      monitoring_service_type
      msp_interval
      msp_address
      device {
        name
        address
      }
    }
    statusPerLocation(serviceId: $id) {
      ... on StatusPerLocationSuccess {
        items {
          id
          monitoring_group {
            id
            location
          }
          monitoring_server {
            id
            name
            location
            address_ipv4
            address_ipv6
          }
          is_nagios
          latest_alert {
            id
            state
            created_at
            is_up
            output
          }
        }
        total
      }
      ... on ErrorResponse {
        status
        message
      }
    }
    monitoringServers: monitoringServersForService(serviceId: $id) {
      id
      is_private
      is_nagios
      location
      country
      name
    }
  }
`;

export interface MonitoringServiceTypesResponse {
  monitoringServiceTypes: ServiceGroup[];
}

export const GQL_MONITORING_SERVICE_TYPES = gql`
  query MonitoringServiceTypes {
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
  }
`;

export const GQL_TOTAL_CHECKS = gql`
  query ListServices($search: String, $limit: Int) {
    values: services(search: $search, limit: $limit) {
      total
      items {
        key: id
        display: name
      }
    }
  }
`;

export const GQL_GLOBAL_PING_NETWORK_LOCATIONS_QUERY = gql`
  query GlobalPingNetworkLocations($search: String, $limit: Int) {
    response: globalPingNetworkLocations(search: $search, limit: $limit) {
      total
      items {
        continent_code
        continent_name
        region_name
        country_code
        country_name
        state_code
        city
        probe_count
      }
    }
  }
`;

export const GQL_GLOBAL_PING_NETWORKS_QUERY = gql`
  query GlobalPingNetworks($search: String, $limit: Int, $location_codes: [GlobalPingNetworkLocationInput!]) {
    response: globalPingNetworks(search: $search, limit: $limit, location_codes: $location_codes) {
      total
      items {
        code
        name
        network_type
        probe_count
      }
    }
  }
`;
